import type {
  AmazonBuilding,
  AmazonNeed,
  OpenPoint,
} from "../../api/hooks/useAmazonOverview";
import { reviewAttrs } from "../../utils/reviewState";
import { STEPS } from "./pathSteps";
import { isSettled } from "./progress";

/** Owed by Amazon; an API deployed before ``from_amazon`` names Amazon as the supplier. */
export const fromAmazon = (n: AmazonNeed): boolean => n.from_amazon ?? n.supplier === "Amazon";

/** What an item carries so text selected on it is a comment on that building's item, for
 * its keeper to apply (docs/Y2026-Q4/design-amazon-item-comments.md). Step 3 and the open
 * issues list both mark items with it, so the agent reads a comment the same from either.
 * The building itself is the ``data-review-doc`` the item sits in. */
export const itemReviewAttrs = (
  siteCode: string,
  n: Pick<AmazonNeed, "key" | "text" | "keeper_agent" | "point_id">,
): Record<string, string> =>
  reviewAttrs("amazon_item", `${siteCode} · ${n.text}`.slice(0, 200), {
    agent: n.keeper_agent ?? "",
    // A point by its own id, as the agent's tools name it (AMAZON_ITEMS).
    itemId: n.point_id ? `point:${n.point_id}` : n.key,
  });

/** Still to come: not received, or partially received, which is owed like it
 * (amazon_items.Item.owed; Noaa, 20 Oct 2026). */
export const owed = (n: AmazonNeed): boolean => n.status === "missing" || n.status === "partial";

/** What Step 3 shows of a building's items from Amazon: those it still needs today, not
 * received, not ruled out for it, and for a step not already done or not applicable
 * there. The backend already reads an item nobody has asked for as not received; an
 * existing-PV item nobody has opened stays out, since nothing yet says whether the
 * building has PV. Step 1 keeps the general list of everything ever needed. */
export const stillNeeded = (b: AmazonBuilding, n: AmazonNeed): boolean =>
  owed(n) && !isSettled(b, n.step);

/** What a blocker card lists: every item for a step not yet done or not applicable, each
 * received, not received or not applicable here (Noaa, 19 Oct 2026), so the card reads
 * the same count as its step ("1 received, 22 not received"). */
export const onCard = (b: AmazonBuilding, n: AmazonNeed): boolean => !isSettled(b, n.step);

/** "N received, P partially received, M not received, K not applicable", each part only
 * when there is one. */
export const stillNeededText = (needs: AmazonNeed[]): string => {
  const count = (status: AmazonNeed["status"]) => needs.filter((n) => n.status === status).length;
  return [
    count("received") ? `${count("received")} received` : "",
    count("partial") ? `${count("partial")} partially received` : "",
    count("missing") ? `${count("missing")} not received` : "",
    count("not_needed") ? `${count("not_needed")} not applicable` : "",
  ]
    .filter(Boolean)
    .join(", ");
};

/** The steps Amazon's design inputs feed (amazon_overview.INPUT_STEPS). */
export const INPUT_STEPS = ["design"];

/** The items the "Amazon design inputs" step counts (``design_input``: Amazon's, for a
 * step in INPUT_STEPS); must agree with amazon_overview._inputs_step: an item for a step
 * already done or not needed here no longer counts. */
export const countedInputs = (
  needs: AmazonNeed[],
  settled: (step: string) => boolean,
): AmazonNeed[] => needs.filter((n) => n.design_input && !settled(n.step));

/** The design inputs ``b`` counts, split as its count is. */
export const designInputs = (
  b: AmazonBuilding,
): { received: AmazonNeed[]; missing: AmazonNeed[] } => {
  const counted = countedInputs(b.needs ?? [], (step) => isSettled(b, step));
  return {
    received: counted.filter((n) => n.status === "received"),
    missing: counted.filter(owed),
  };
};

/** An item not received that its keeper says holds work up, or that something waits for,
 * holds up the step it is for; as amazon_items.blocking, so an override of its status
 * blocks or frees that step the way the backend would. */
export const blocksItsStep = (n: AmazonNeed): boolean =>
  owed(n) && (n.blocking ?? []).length > 0;

/** The items that hold their step up first, the rest after in their own order
 * (Noaa, 19 Oct 2026). */
export const holdersFirst = (items: AmazonNeed[]): AmazonNeed[] => [
  ...items.filter(blocksItsStep),
  ...items.filter((n) => !blocksItsStep(n)),
];

/** A feedback point owed by someone on record other than InRange, as
 * readiness_points.owed_outside reads it: the Step 3 card and the Step 2 hover card list
 * it with what is needed from that party. */
export const owedOutside = (p: OpenPoint): boolean => !!p.owed_by && p.owed_party !== "inrange";

/** The open feedback points the other side owes on ``stepIds`` not yet done or not
 * applicable, each as an item not received for its step: a point still open is something
 * not received yet, listed and counted like any other (Noaa, 19 Oct 2026). It holds its
 * step up, which cannot finish while one is open (amazon_overview._points_wait). */
const neededHere = (b: AmazonBuilding, key: string): boolean =>
  (b.needs ?? []).some((n) => n.key === key && stillNeeded(b, n));

export const pointNeeds = (b: AmazonBuilding, stepIds: string[]): AmazonNeed[] =>
  stepIds
    .filter((id) => !isSettled(b, id))
    .flatMap((id) =>
      // A point naming an item still needed here is that item's, listed once in its place;
      // otherwise (the item came, is not needed, or its step is done) it keeps its own row.
      (b.steps[id]?.open_points ?? [])
        .filter((p) => owedOutside(p) && !(p.need && neededHere(b, p.need)))
        .map(
        (p, i): AmazonNeed => ({
          // By step and place, as the open issues list groups the same point across sites.
          key: `point:${id}:${i}`,
          point_id: p.point_id,
          point_from: { raised_by: p.raised_by, raised_on: p.raised_on, source: p.source },
          step: id,
          text: p.text,
          clause: "",
          only_where: "",
          status: "missing",
          date: "",
          evidence: "",
          design_input: false,
          listed: false,
          supplier: p.owed_party === "client" ? "Amazon" : p.owed_by,
          from_amazon: p.owed_party === "client" || !p.owed_party,
          keeper_agent: p.agent ?? "",
          blocking: ["the step cannot finish until it is answered"],
        }),
      ),
    );

export const itemCount = (n: number): string => `${n} ${n === 1 ? "item" : "items"}`;

/** What ``stepId`` still waits on, as its "Needed from Amazon" list shows it: the open
 * feedback points the other side owes on it, then its items still needed. Every count of
 * what a step waits on reads this, so a count never disagrees with the list. */
const stillToCome = (b: AmazonBuilding, stepId: string): AmazonNeed[] => [
  ...pointNeeds(b, [stepId]),
  ...(b.needs ?? []).filter((n) => n.step === stepId && stillNeeded(b, n)),
];

/** The items still needed for ``stepId`` that hold it up, its own first, then the design
 * inputs it needs: the ones ``waitingText`` counts as holding it up, by name. */
export const heldItems = (b: AmazonBuilding, stepId: string): AmazonNeed[] => {
  const held = stillToCome(b, stepId).filter(blocksItsStep);
  return [...held.filter((n) => !n.design_input), ...held.filter((n) => n.design_input)];
};

/** The reasons an item holds its step up, as one sentence after "Why:": the reason
 * alone, never who decided it or when (Noaa, 19 Oct 2026). */
export const whyText = (n: AmazonNeed): string =>
  (n.blocking ?? []).map((r) => r.trim().replace(/[.\s]+$/, "")).join("; ");

const holdText = (n: number): string => (n ? `, ${n} ${n === 1 ? "holds" : "hold"} this up` : "");

/** What ``stepId`` waits on from others, in one phrase every surface uses (Noaa, 19 Oct
 * 2026): "Waiting on Amazon: 6 items, 1 holds this up · Amazon design inputs: 3 items",
 * its own items by who owes them, then the design inputs it needs. Each count is the items
 * still needed for the step, as its list shows them; the part that holds it up is the
 * subset its keeper says it cannot go on without, or that something else waits for. ""
 * where nothing is still needed. */
export const waitingText = (b: AmazonBuilding, stepId: string): string => {
  const still = stillToCome(b, stepId);
  const byParty = new Map<string, AmazonNeed[]>();
  for (const n of still.filter((x) => !x.design_input)) {
    const party = n.supplier || "Amazon";
    byParty.set(party, [...(byParty.get(party) ?? []), n]);
  }
  const inputs = still.filter((n) => n.design_input);
  const parts = [
    ...[...byParty].map(
      ([party, items]) =>
        `${party}: ${itemCount(items.length)}${holdText(items.filter(blocksItsStep).length)}`,
    ),
    inputs.length
      ? `Amazon design inputs: ${itemCount(inputs.length)}${holdText(inputs.filter(blocksItsStep).length)}`
      : "",
  ].filter(Boolean);
  return parts.length ? `Waiting on ${parts.join(" · ")}` : "";
};

export const NEED_STATUS: Record<AmazonNeed["status"], string> = {
  received: "Received",
  partial: "Partially received",
  missing: "Not received",
  not_needed: "Not applicable",
};

/** A list of items in the order Step 3's card and Step 2's hover card both list them: not
 * received, partially received (Noaa, 20 Oct 2026), then received, each split by its category in its step's list, those holding the
 * step up first among the ones still to come (Noaa, 19 Oct 2026). An item for a step drawn
 * inside the card's box whose list has none goes under that step's name. Those with no
 * group (an open feedback point, or a list with no categories) come first, unheaded, and
 * "Other" last. Those not applicable here are apart, to fold below. */
export const RUNS: {
  label: string;
  received?: boolean;
  partial?: boolean;
  holds: (n: AmazonNeed) => boolean;
}[] = [
  { label: "Not received", holds: (n) => n.status === "missing" },
  { label: "Partially received", partial: true, holds: (n) => n.status === "partial" },
  { label: "Received", received: true, holds: (n) => n.status === "received" },
];

export interface NeededRuns {
  runs: { run: (typeof RUNS)[number]; groups: [string, AmazonNeed[]][] }[];
  notNeeded: [string, AmazonNeed[]][];
}

const OTHER_CATEGORY = "Other";

/** ``items`` split by ``groupOf``, groups in the order they first appear: the unnamed
 * group first and "Other" last. Step 1's list and Step 3's card both group this way. */
export const inGroups = <T>(
  items: T[],
  groupOf: (item: T) => string,
  order: string[] = items.map(groupOf),
): [string, T[]][] => {
  const named = [...new Set(order)];
  return [
    ...named.filter((c) => c === ""),
    ...named.filter((c) => c !== "" && c !== OTHER_CATEGORY),
    ...named.filter((c) => c === OTHER_CATEGORY),
  ]
    .map((c): [string, T[]] => [c, items.filter((n) => groupOf(n) === c)])
    .filter(([, group]) => group.length > 0);
};

/** The categories a step's list groups its needs by, "Other" last; none where its list
 * has none. Must agree with amazon_needs.step_categories over the code list. */
const stepCategories = (stepId: string): string[] => {
  const named = [
    ...new Set(
      (STEPS.find((s) => s.id === stepId)?.needs ?? []).flatMap((n) =>
        n.category ? [n.category] : [],
      ),
    ),
  ];
  return named.length
    ? [...named.filter((c) => c !== OTHER_CATEGORY), OTHER_CATEGORY]
    : [];
};

/** Where a need sits in its step's list on Step 1: its category (the page's own, or the
 * one the backend places a saved line under, amazon_blueprint.placed_steps), else
 * "Other" as on Step 3's card; "" where the list has none. */
export const needCategory = (
  stepId: string,
  need: { category?: string },
): string => {
  const categories = stepCategories(stepId);
  if (!categories.length) return "";
  return need.category && categories.includes(need.category)
    ? need.category
    : OTHER_CATEGORY;
};

export const neededRuns = (
  items: AmazonNeed[],
  stepId: string | null,
  stepTitle: (id: string) => string,
): NeededRuns => {
  const groupOf = (n: AmazonNeed): string =>
    n.category || (n.step !== stepId ? stepTitle(n.step) : "");
  const order = items.map(groupOf);
  const byGroup = (list: AmazonNeed[]): [string, AmazonNeed[]][] =>
    inGroups(list, groupOf, order);
  return {
    runs: RUNS.map((run) => ({
      run,
      groups: byGroup(items.filter(run.holds)).map(
        ([c, list]): [string, AmazonNeed[]] => [c, run.received ? list : holdersFirst(list)],
      ),
    })).filter((r) => r.groups.length > 0),
    notNeeded: byGroup(items.filter((n) => n.status === "not_needed")),
  };
};

/** Everything Step 2's card for step ``id`` lists as needed from Amazon, as Step 3's card
 * for it does: Amazon's design inputs for that step, else the step's own items on the card
 * and its open feedback points, each a point not received (``pointNeeds``). */
export const stepItems = (b: AmazonBuilding, id: string): AmazonNeed[] => {
  if (id === "inputs") {
    const inputs = designInputs(b);
    return [...inputs.missing, ...inputs.received];
  }
  return [
    ...pointNeeds(b, [id]),
    ...(b.needs ?? []).filter((n) => n.step === id && onCard(b, n)),
  ];
};
